/**
 * GRIDHEIMR — Hatch Button Component
 *
 * Prominent button for generating new personas in the Hatchery Lab.
 */

import React, { useState } from "react";
import type { HatchResponse } from "../api/types";
import { getErrorMessage } from "../api/client";
import { BLUE, YELLOW, DARK_BG, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface HatchButtonProps {
  onHatch?: (creatorName: string) => Promise<HatchResponse | null>;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Hatch Button Component
 *
 * Large, prominent button for hatching new agent personas.
 * Prompts for creator name and handles the hatching process.
 */
export function HatchButton({ onHatch, loading = false, disabled = false }: HatchButtonProps) {
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [creatorName, setCreatorName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [hatching, setHatching] = useState(false);

  const isDisabled = disabled || loading || hatching;

  /**
   * Handle button click - show name prompt
   */
  const handleClick = () => {
    setShowNamePrompt(true);
    setError(null);
    setCreatorName("");
  };

  /**
   * Handle cancel - close prompt
   */
  const handleCancel = () => {
    setShowNamePrompt(false);
    setCreatorName("");
    setError(null);
  };

  /**
   * Handle hatch - call API and close prompt
   */
  const handleHatch = async () => {
    if (!creatorName.trim()) {
      setError("Please enter your name");
      return;
    }

    if (!onHatch) {
      setError("Hatch function not provided");
      return;
    }

    setHatching(true);
    setError(null);

    try {
      const response = await onHatch(creatorName.trim());

      if (response) {
        // Success - close prompt and reset
        setShowNamePrompt(false);
        setCreatorName("");
      } else {
        setError("Failed to hatch persona. Please try again.");
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setHatching(false);
    }
  };

  /**
   * Handle Enter key in input
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !hatching) {
      handleHatch();
    }
  };

  return (
    <div style={styles.container}>
      {/* Error Message */}
      {error && (
        <div style={styles.errorBanner}>
          <span style={styles.errorIcon}>⚠️</span>
          <span style={styles.errorText}>{error}</span>
          <button
            style={styles.dismissButton}
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {/* Main Hatch Button */}
      {!showNamePrompt && (
        <button
          style={{
            ...styles.hatchButton,
            ...(isDisabled ? styles.hatchButtonDisabled : {}),
          }}
          onClick={handleClick}
          disabled={isDisabled}
          aria-label="Hatch new persona"
        >
          <span style={styles.hatchIcon}>🥚✨</span>
          <span style={styles.hatchText}>
            {loading || hatching ? "Hatching..." : "Hatch New Persona"}
          </span>
        </button>
      )}

      {/* Name Prompt Dialog */}
      {showNamePrompt && (
        <div style={styles.promptBox}>
          <h3 style={styles.promptTitle}>Hatch Your Persona</h3>
          <p style={styles.promptDescription}>
            Enter your name to create a unique AI persona with a randomized personality profile.
          </p>

          <input
            type="text"
            style={styles.nameInput}
            placeholder="Your name"
            value={creatorName}
            onChange={(e) => setCreatorName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={hatching}
            autoFocus
            maxLength={50}
          />

          <div style={styles.promptButtons}>
            <button
              style={{ ...styles.promptButton, ...styles.cancelButton }}
              onClick={handleCancel}
              disabled={hatching}
            >
              Cancel
            </button>
            <button
              style={{ ...styles.promptButton, ...styles.confirmButton }}
              onClick={handleHatch}
              disabled={hatching || !creatorName.trim()}
            >
              {hatching ? "Hatching..." : "🐣 Hatch!"}
            </button>
          </div>
        </div>
      )}

      {/* Help Text */}
      {!showNamePrompt && (
        <p style={styles.helpText}>
          Generate a new AI persona with a unique personality profile, appearance,
          voice characteristics, and psychological traits.
        </p>
      )}
    </div>
  );
}

/**
 * Component styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "24px",
    width: "100%",
    maxWidth: "600px",
    margin: "0 auto",
  },

  // Error Banner
  errorBanner: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    background: "rgba(239,68,68,0.12)",
    border: "1px solid rgba(239,68,68,0.4)",
    color: "#fca5a5",
  },
  errorIcon: {
    fontSize: "20px",
    flexShrink: 0,
  },
  errorText: {
    flex: 1,
    fontSize: "14px",
    fontFamily: MONO,
    fontWeight: 500,
  },
  dismissButton: {
    background: "none",
    border: "none",
    color: "#fca5a5",
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px 8px",
    transition: "opacity 0.2s",
  },

  // Main Hatch Button
  hatchButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "20px 40px",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    color: "#000",
    background: YELLOW,
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: `0 0 20px rgba(254,204,0,0.35), 3px 3px 0 ${BLUE}`,
    minWidth: "280px",
    letterSpacing: "0.1em",
  },
  hatchButtonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
    boxShadow: `0 0 8px rgba(254,204,0,0.15)`,
  },
  hatchIcon: {
    fontSize: "24px",
  },
  hatchText: {
    letterSpacing: "0.1em",
  },

  // Name Prompt Dialog
  promptBox: {
    width: "100%",
    padding: "24px",
    background: DARK_BG,
    border: `2px solid ${BLUE}`,
    boxShadow: `0 0 30px rgba(0,106,167,0.2)`,
  },
  promptTitle: {
    margin: "0 0 12px 0",
    fontFamily: PIXEL,
    fontSize: "0.6rem",
    fontWeight: 400,
    color: YELLOW,
    textAlign: "center",
    letterSpacing: "0.08em",
    textShadow: `0 0 10px rgba(254,204,0,0.3)`,
  },
  promptDescription: {
    margin: "0 0 20px 0",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.5)",
    textAlign: "center",
    lineHeight: 1.5,
  },
  nameInput: {
    width: "100%",
    padding: "12px 16px",
    fontFamily: MONO,
    fontSize: "0.9rem",
    color: "#fff",
    background: "rgba(0,106,167,0.06)",
    border: `1px solid rgba(0,106,167,0.25)`,
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
    marginBottom: "20px",
  },
  promptButtons: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
  },
  promptButton: {
    padding: "12px 24px",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    border: "2px solid",
    cursor: "pointer",
    transition: "all 0.2s",
    minWidth: "120px",
    letterSpacing: "0.08em",
  },
  cancelButton: {
    background: "transparent",
    borderColor: "rgba(255,255,255,0.2)",
    color: "rgba(255,255,255,0.5)",
  },
  confirmButton: {
    background: "transparent",
    borderColor: YELLOW,
    color: YELLOW,
    boxShadow: `0 0 10px rgba(254,204,0,0.2)`,
  },

  // Help Text
  helpText: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.4)",
    textAlign: "center",
    maxWidth: "500px",
    lineHeight: 1.6,
  },
};
